import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  Paper,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";

import { PERMISSIONS } from "../../constants";
import {
  applicantDocPublicUrl,
  getApplicant,
  getApplicantLogs,
  getApplications,
  selectContractor,
} from "../../services/backend/ApplicantService";
import {
  getApplicantConfig,
  DEFAULT_APPLICANT_CONFIG,
  labelFor,
  profileDocForSection,
  profileDocLabel,
  profileDocs,
  requiredProfileDocs,
} from "../../services/backend/ApplicantConfigService";
import ApplicantDocsValidation from "./ApplicantDocsValidation";
import ApplicantLogsDrawer from "./ApplicantLogsDrawer";
import {
  PROFILE_SECTIONS,
  computeProfileCompletion,
  docStatusRollup,
  entryNeedsEvidence,
  experienceMonths,
  experiencePeriods,
  filesForEntry,
  formatDuration,
  formatPeriodDuration,
  normalizeDoc,
  normalizeEducation,
  normalizeWorkExperience,
  periodDuration,
  recomputeProfileStatus,
  totalExperienceMonths,
} from "./applicantLogic";
import { DOC_FILE_STATUS_LABELS, ESTADO_DOC_LABELS, labelForStatus } from "./applicantLabels";
import { IDENTITY_WARNINGS, applicantScreening, fileIdentity } from "./identityCheck";
import { ScreeningChip } from "./IdentityNotice";
import { chipsTone, toneRowSx } from "./rowTone";

// Estado del archivo de soporte → color del Chip en la fila contraída.
const ENTRY_DOC_COLOR = {
  UPLOADED: "info",
  VALIDATED: "success",
  REJECTED: "error",
};

// Estado → side-rail status dot color.
const ESTADO_DOT = {
  VALIDATED: "success.main",
  COMPLETE: "info.main",
  INCOMPLETE: "warning.main",
  PENDING: "grey.400",
};

const Field = ({ label, value }) => (
  <Grid item xs={12} sm={6} md={3}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="body2">{value || "—"}</Typography>
  </Grid>
);

// Las fechas se guardan en ISO; aquí solo interesa el día.
const fecha = (d) => (d ? String(d).slice(0, 10) : "");

// Una formación / experiencia en la ficha del gestor: contraída por defecto, con
// lo justo en el encabezado para revisar la lista de un vistazo — qué es, en qué
// estado está su soporte y las señales que no se pueden pasar por alto (título
// sin convalidar, estudios en curso, empleo vigente). Al abrirla aparecen los
// campos completos y la validación de su documento.
function ReviewEntry({ label, summary, chips = [], children }) {
  const [open, setOpen] = useState(false);
  return (
    <Box sx={{ mb: 1.5, border: 1, borderColor: "divider", borderRadius: 1, overflow: "hidden" }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        onClick={() => setOpen((v) => !v)}
        sx={{ px: 1, py: 1, cursor: "pointer", ...toneRowSx(chipsTone(chips)) }}
      >
        <IconButton size="small" aria-label={`${open ? "Contraer" : "Expandir"} ${label}`}>
          {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
          <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
          <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>{summary}</Typography>
        </Box>
        <Stack direction="row" spacing={0.5} flexShrink={0}>
          {chips.filter(Boolean)}
        </Stack>
      </Stack>
      <Collapse in={open}>
        <Box sx={{ px: 2, pb: 2, pt: 0.5 }}>{children}</Box>
      </Collapse>
    </Box>
  );
}

// El aspirante puede registrar más estudios/experiencias de los que certifica.
// Esta advertencia le dice al revisor cuántos registros deben soporte y no lo
// tienen: los que están «en curso» o «actualmente» no deben ninguno.
const EvidenceGap = ({ faltan, noun }) => {
  if (!faltan) return null;
  return (
    <Alert severity="warning" sx={{ mb: 2 }}>
      {faltan} {faltan === 1 ? noun.singular : noun.plural} sin su soporte documental:
      no {faltan === 1 ? "debería tenerse" : "deberían tenerse"} en cuenta.
    </Alert>
  );
};

export default function ApplicantView() {
  const { nuip } = useParams();
  const navigate = useNavigate();
  // Convocatoria de la que se llegó (listado por convocatoria): abre esa
  // postulación y conserva el alcance al volver.
  const [searchParams] = useSearchParams();
  const fromCall = searchParams.get("call");
  const backToList = fromCall
    ? `/applicants?call=${encodeURIComponent(fromCall)}`
    : "/applicants";
  const user = useSelector((state) => state.user.value);
  const perms = user?.permissions || [];

  const [applicant, setApplicant] = useState(null);
  const [applications, setApplications] = useState([]);
  const [selectedPostId, setSelectedPostId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selecting, setSelecting] = useState(false);
  const [selectedOk, setSelectedOk] = useState(false);
  const [toast, setToast] = useState(null);
  // Needed for storageBucket — the applicant stores a photo *path*, not a URL.
  const [cfg, setCfg] = useState(DEFAULT_APPLICANT_CONFIG);

  // Bitácora — se pide sólo al abrirla: es una lectura extra que la mayoría de
  // las revisiones no necesita.
  const [logsOpen, setLogsOpen] = useState(false);
  const [logs, setLogs] = useState(null);
  const [logsLoading, setLogsLoading] = useState(false);

  const canSelect = perms.includes(PERMISSIONS.APPLICANT.SELECT);
  // La bitácora es sólo para ADMIN. Se comprueba el ROL y no un permiso porque
  // los permisos son una foto guardada por usuario: un permiso nuevo no existiría
  // en las cuentas ya creadas y la pestaña no le aparecería a ningún admin actual.
  // Mismo criterio que la configuración del módulo (ExtensionHub).
  const canViewLogs = (user?.roles || []).includes("ADMIN");

  const openLogs = async () => {
    setLogsOpen(true);
    if (logs !== null) return; // ya cargada
    setLogsLoading(true);
    setLogs(await getApplicantLogs(nuip, user));
    setLogsLoading(false);
  };

  const reload = async () => {
    setLoading(true);
    const [a, ps, c] = await Promise.all([getApplicant(nuip), getApplications(nuip), getApplicantConfig()]);
    setApplicant(a);
    setApplications(ps);
    setCfg(c);
    // Keep the current selection if it still exists; else honour the convocatoria
    // we arrived from; else default to General/first.
    setSelectedPostId((prev) => {
      if (prev && ps.some((p) => p.callId === prev)) return prev;
      if (fromCall && ps.some((p) => p.callId === fromCall)) return fromCall;
      return (ps.find((p) => p.callId === "general") || ps[0])?.callId || null;
    });
    setLoading(false);
  };

  useEffect(() => {
    if (!perms.includes(PERMISSIONS.APPLICANT.READ)) {
      navigate("/home");
      return;
    }
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nuip]);

  // Documentos exigidos del perfil y, en los atados, los registros que deben
  // cubrir. Se arma sobre unos datos concretos para poder recalcular la
  // completitud tras una validación sin volver a leer el aspirante.
  const buildProfileContext = (data) =>
    Object.fromEntries(
      requiredProfileDocs(cfg).map((key) => {
        const section = (profileDocs(cfg).find((d) => d.key === key) || {}).boundTo || null;
        if (!section) return [key, {}];
        const entries = section === PROFILE_SECTIONS.EDUCATION
          ? normalizeEducation(data?.education)
          : normalizeWorkExperience(data?.workExperience);
        return [key, { section, entries }];
      })
    );

  // Validar o rechazar un documento NO recarga la ficha: recargarla perdería el
  // scroll y volvería a contraer todas las filas justo cuando el gestor va por
  // la mitad de la revisión. El backend devuelve el mapa de documentos y el
  // estado ya recalculados, así que se aplican sobre el estado local; la
  // completitud se recalcula aquí con la misma lógica que usó el servidor.
  // Reconcilia con el servidor sin tocar `loading`: nada se desmonta, así que el
  // scroll y las filas abiertas se conservan. El parche local de abajo da la
  // respuesta inmediata; esto garantiza que ningún campo derivado (el estado que
  // habilita «Seleccionar», por ejemplo) se quede atrás.
  const refreshQuiet = async () => {
    const [a, ps] = await Promise.all([getApplicant(nuip), getApplications(nuip)]);
    if (a) setApplicant(a);
    if (ps) setApplications(ps);
    // La decisión que se acaba de tomar quedó en la bitácora: se invalida para
    // que al abrirla no muestre una copia anterior a esta misma validación.
    setLogs(null);
  };

  const applyValidation = (scope, callId) => (result) => {
    if (!result || !result.documents) return;
    if (scope === "profile") {
      setApplicant((prev) => {
        if (!prev) return prev;
        const next = {
          ...prev,
          profileDocuments: result.documents,
          documentationStatus: result.documentationStatus,
        };
        next.completionPercentage = computeProfileCompletion(
          next, result.documents, requiredProfileDocs(cfg), buildProfileContext(next)
        );
        return next;
      });
    } else {
      setApplications((prev) => prev.map((p) => (
        p.callId === callId
          ? { ...p, documents: result.documents, documentationStatus: result.documentationStatus }
          : p
      )));
    }
    refreshQuiet();
  };

  const handleSelect = async () => {
    setSelecting(true);
    const contractor = await selectContractor(nuip, user, { callId: selectedPostId });
    setSelecting(false);
    if (!contractor) {
      setToast({ severity: "error", msg: "No fue posible seleccionar al aspirante como contratista." });
      return;
    }
    // La selección termina aquí: el backend ya marcó la postulación como SELECTED y
    // envió el correo de etapa 2 pidiendo los documentos restantes. El contrato se
    // crea después, por el flujo normal de contratos.
    setSelectedOk(true);
  };

  const handleCloseSelected = () => {
    setSelectedOk(false);
    reload();
  };

  if (loading) {
    return (
      <Container sx={{ py: 6, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!applicant) {
    return (
      <Container sx={{ py: 6 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(backToList)}>Volver</Button>
        <Alert severity="warning" sx={{ mt: 2 }}>Aspirante no encontrado.</Alert>
      </Container>
    );
  }

  const formacion = normalizeEducation(applicant.education);
  const experiencia = normalizeWorkExperience(applicant.workExperience);
  const nombreCompleto = `${applicant.firstName || ""} ${applicant.lastName || ""}`.trim();
  // Avatar falls back to initials when there's no photo (or it fails to load).
  const iniciales = `${(applicant.firstName || "").charAt(0)}${(applicant.lastName || "").charAt(0)}`.toUpperCase();
  const fotoUrl = applicantDocPublicUrl(cfg.storageBucket, applicant.photoPath);
  const selectedPost = applications.find((p) => p.callId === selectedPostId) || null;

  // Documentos de perfil: pertenecen a la persona, no a la convocatoria. Los dos
  // atados traen el soporte de cada formación / experiencia (file.entryId); el
  // resto (cédula…) se revisan en su propio bloque.
  const profileDocuments = applicant.profileDocuments || {};
  // Documents whose text points to someone else (identityCheck), across the
  // profile and every postulación — the same files the review chip counts —
  // named here so staff see them first.
  const identityIssues = [
    ...Object.entries(profileDocuments).map(([k, d]) => [profileDocLabel(cfg, k), d]),
    ...applications.flatMap((p) => Object.entries(p.documents || {})
      .map(([k, d]) => [`${labelFor(cfg, k)} · ${p.callName || p.callId}`, d])),
  ].flatMap(([label, d]) => normalizeDoc(d).files
    .filter((f) => IDENTITY_WARNINGS.has(fileIdentity(f, applicant)?.status))
    .map((f) => `${label}${f.name ? ` (${f.name})` : ""}`));
  const eduDoc = profileDocForSection(cfg, PROFILE_SECTIONS.EDUCATION);
  const expDoc = profileDocForSection(cfg, PROFILE_SECTIONS.EXPERIENCE);
  const unboundProfileDocs = profileDocs(cfg).filter((d) => !d.boundTo);
  const unboundProfileMap = Object.fromEntries(
    unboundProfileDocs.map((d) => [d.key, profileDocuments[d.key]]).filter(([, v]) => v)
  );
  // Documentos de UN registro, en el formato que espera ApplicantDocsValidation.
  const entryDocMap = (doc, entry) => {
    if (!doc) return {};
    const files = filesForEntry(profileDocuments[doc.key], entry.id);
    return files.length ? { [doc.key]: { type: doc.key, files } } : {};
  };
  // Estado del soporte de UN registro, tal como se ve en la fila contraída.
  const evidenceChip = (doc, entry, section) => {
    if (!doc) return null;
    const files = filesForEntry(profileDocuments[doc.key], entry.id);
    if (files.length) {
      const status = docStatusRollup({ files });
      return (
        <Chip key="ev" size="small" color={ENTRY_DOC_COLOR[status] || "default"}
          label={labelForStatus(DOC_FILE_STATUS_LABELS, status)} />
      );
    }
    if (!entryNeedsEvidence(entry, section)) return null;
    return <Chip key="ev" size="small" color="warning" variant="outlined" label="Sin soporte" />;
  };
  // Its certificate seems to be someone else's: visible on the closed row.
  const identityChip = (doc, entry) => {
    if (!doc) return null;
    const flagged = filesForEntry(profileDocuments[doc.key], entry.id)
      .some((f) => IDENTITY_WARNINGS.has(fileIdentity(f, applicant)?.status));
    return flagged ? <Chip key="id" size="small" color="error" label="Titular no coincide" /> : null;
  };
  const missingEvidence = (doc, entries, section) => {
    if (!doc) return 0;
    return entries.filter((e) => entryNeedsEvidence(e, section) && filesForEntry(profileDocuments[doc.key], e.id).length === 0).length;
  };
  // Para seleccionar hacen falta las dos mitades, igual que en el backend: la
  // hoja de vida (documentos de perfil) y los documentos propios de la
  // convocatoria, si es que pide alguno.
  const profileContext = Object.fromEntries(
    requiredProfileDocs(cfg).map((key) => {
      const section = (profileDocs(cfg).find((d) => d.key === key) || {}).boundTo || null;
      return [key, section ? { section, entries: section === PROFILE_SECTIONS.EDUCATION ? formacion : experiencia } : {}];
    })
  );
  const perfilValidado = recomputeProfileStatus(profileDocuments, requiredProfileDocs(cfg), profileContext) === "VALIDATED";
  const callRequiresDocs = Object.keys(selectedPost?.documents || {}).length > 0;
  const callValidado = !callRequiresDocs || selectedPost?.documentationStatus === "VALIDATED";
  const docsValidados = perfilValidado && callValidado;
  const yaSeleccionado = selectedPost?.selected === true || selectedPost?.status === "SELECTED";

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(backToList)}>Volver</Button>
        {canSelect && (
          <Tooltip
            title={
              yaSeleccionado
                ? "El aspirante ya fue seleccionado en esta postulación"
                : docsValidados
                  ? ""
                  : !perfilValidado
                    ? "Faltan documentos del aspirante por validar (hoja de vida y soportes)"
                    : "Faltan documentos de esta convocatoria por validar"
            }
          >
            <span>
              <Button
                variant="contained"
                startIcon={<PersonAddIcon />}
                onClick={handleSelect}
                disabled={selecting || !docsValidados || yaSeleccionado}
              >
                {yaSeleccionado
                  ? "Ya seleccionado"
                  : selecting
                    ? "Seleccionando…"
                    : "Seleccionar como contratista"}
              </Button>
            </span>
          </Tooltip>
        )}
      </Stack>

      <Paper sx={{ p: 3, mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
          <Avatar
            alt={nombreCompleto}
            src={fotoUrl || undefined}
            sx={{ width: 72, height: 72, bgcolor: "primary.main", fontSize: "1.5rem" }}
          >
            {iniciales || "?"}
          </Avatar>
          <Typography variant="h5">
            {nombreCompleto || applicant.nuip}
          </Typography>
          <Chip
            label={labelForStatus(ESTADO_DOC_LABELS, applicant.documentationStatus)}
            color={docsValidados ? "success" : "default"}
            size="small"
          />
          <Chip label={`${applicant.completionPercentage || 0}% completo`} size="small" variant="outlined" />
          <ScreeningChip screening={applicantScreening(applicant, applications)} />
        </Stack>
        <Grid container spacing={2}>
          <Field label="Cédula" value={applicant.nuip} />
          <Field label="Email" value={applicant.email} />
          <Field label="Teléfono" value={applicant.phone} />
          <Field label="Celular" value={applicant.mobilePhone} />
          <Field label="Dirección" value={applicant.address} />
        </Grid>
        {identityIssues.length > 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {identityIssues.length === 1
              ? "1 documento no parece ser de este aspirante"
              : `${identityIssues.length} documentos no parecen ser de este aspirante`}
            {" "}(no aparece su cédula, o aparece su nombre con otro número): {identityIssues.join(" · ")}.
            Revísalos antes de validarlos.
          </Alert>
        )}
      </Paper>

      {/* Documentos de la persona que no cuelgan de un registro (cédula…). */}
      {unboundProfileDocs.length > 0 && (
        <Paper sx={{ p: 3, mb: 2 }}>
          <Typography variant="h6" gutterBottom>Documentos del aspirante</Typography>
          <Divider sx={{ mb: 2 }} />
          <ApplicantDocsValidation
            nuip={applicant.nuip}
            person={applicant}
            documents={unboundProfileMap}
            scope="profile"
            title={null}
            emptyText="Sin documentos personales cargados."
            onChange={applyValidation("profile")}
          />
        </Paper>
      )}

      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="h6" gutterBottom>Formación académica ({formacion.length})</Typography>
        <Divider sx={{ mb: 2 }} />
        <EvidenceGap
          faltan={missingEvidence(eduDoc, formacion, PROFILE_SECTIONS.EDUCATION)}
          noun={{ singular: "formación registrada", plural: "formaciones registradas" }}
        />
        {formacion.length === 0 ? (
          <Typography variant="body2" color="text.secondary">Sin formación registrada.</Typography>
        ) : (
          formacion.map((e, i) => (
            <ReviewEntry
              key={e.id || i}
              label={`Formación ${i + 1}`}
              summary={[e.degree, e.institution].filter(Boolean).join(" · ") || "Sin datos"}
              chips={[
                evidenceChip(eduDoc, e, PROFILE_SECTIONS.EDUCATION),
                identityChip(eduDoc, e),
                e.inProgress ? <Chip key="curso" size="small" variant="outlined" label="En curso" /> : null,
                // Un título del exterior sólo es válido si está convalidado ante
                // el MEN: el revisor tiene que verlo sin abrir la fila.
                e.foreignDegree ? (
                  <Chip key="conv" size="small" color={e.convalidated ? "success" : "warning"}
                    label={e.convalidated ? "Exterior · convalidado" : "Exterior · SIN convalidar"} />
                ) : null,
              ]}
            >
              <Grid container spacing={2}>
                <Field label="Nivel" value={e.level} />
                <Field label="Título" value={e.degree} />
                <Field label="Institución" value={e.institution} />
                <Field
                  label="Fecha de grado"
                  value={e.inProgress ? "En curso" : fecha(e.graduationDate)}
                />
              </Grid>
              {/* El soporte de ESTA formación, validable aquí mismo. */}
              <Box sx={{ mt: 1 }}>
                <ApplicantDocsValidation
                  nuip={applicant.nuip}
                  person={applicant}
                  documents={entryDocMap(eduDoc, e)}
                  scope="profile"
                  title={null}
                  emptyText={entryNeedsEvidence(e, PROFILE_SECTIONS.EDUCATION)
                    ? "Sin acta de grado cargada."
                    : "En curso: no se exige acta de grado."}
                  onChange={applyValidation("profile")}
                />
              </Box>
            </ReviewEntry>
          ))
        )}
      </Paper>

      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="h6" gutterBottom>Experiencia laboral ({experiencia.length})</Typography>
        {totalExperienceMonths(experiencia) > 0 && (
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Experiencia total: <strong>{formatDuration(totalExperienceMonths(experiencia))}</strong>
            {" "}(los periodos que se cruzan se cuentan una sola vez)
          </Typography>
        )}
        <Divider sx={{ mb: 2 }} />
        <EvidenceGap
          faltan={missingEvidence(expDoc, experiencia, PROFILE_SECTIONS.EXPERIENCE)}
          noun={{ singular: "experiencia registrada", plural: "experiencias registradas" }}
        />
        {experiencia.length === 0 ? (
          <Typography variant="body2" color="text.secondary">Sin experiencia registrada.</Typography>
        ) : (
          experiencia.map((e, i) => (
            <ReviewEntry
              key={e.id || i}
              label={`Experiencia ${i + 1}`}
              summary={[e.company, e.position].filter(Boolean).join(" · ") || "Sin datos"}
              chips={[
                evidenceChip(expDoc, e, PROFILE_SECTIONS.EXPERIENCE),
                identityChip(expDoc, e),
                experienceMonths(experiencePeriods(e)) > 0
                  ? <Chip key="tiempo" size="small" variant="outlined" label={formatDuration(experienceMonths(experiencePeriods(e)))} />
                  : null,
                e.current ? <Chip key="actual" size="small" variant="outlined" label="Actualmente" /> : null,
              ]}
            >
              {e.nit && (
                <Typography variant="caption" color="text.secondary" display="block">NIT {e.nit}</Typography>
              )}
              {/* Un certificado puede cubrir varios cargos / periodos en la misma empresa. */}
              {experiencePeriods(e).map((p, pi) => (
                <Typography key={pi} variant="caption" color="text.secondary" display="block">
                  {p.position ? `${p.position}: ` : ""}
                  {fecha(p.startDate) || "—"} → {p.current ? "Actualmente" : (fecha(p.endDate) || "Sin fecha")}
                  {periodDuration(p) && ` · ${formatPeriodDuration(periodDuration(p))}`}
                </Typography>
              ))}
              {e.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{e.description}</Typography>
              )}
              <Box sx={{ mt: 1 }}>
                <ApplicantDocsValidation
                  nuip={applicant.nuip}
                  person={applicant}
                  documents={entryDocMap(expDoc, e)}
                  scope="profile"
                  title={null}
                  emptyText={entryNeedsEvidence(e, PROFILE_SECTIONS.EXPERIENCE)
                    ? "Sin certificado laboral cargado."
                    : "Empleo actual: no se exige certificado de terminación."}
                  onChange={applyValidation("profile")}
                />
              </Box>
            </ReviewEntry>
          ))
        )}
      </Paper>

      <Paper sx={{ p: { xs: 2, md: 3 }, mb: 2 }}>
        <Typography variant="h6" gutterBottom>Postulaciones</Typography>
        <Grid container spacing={2}>
          {/* Vertical side rail — one row per postulación the aspirant is in. */}
          <Grid item xs={12} md={3}>
            <Stack spacing={1}>
              {applications.length === 0 && (
                <Typography variant="body2" color="text.secondary">Sin postulaciones.</Typography>
              )}
              {applications.map((p) => {
                const active = p.callId === selectedPostId;
                return (
                  <Box
                    key={p.callId}
                    onClick={() => setSelectedPostId(p.callId)}
                    sx={{
                      cursor: "pointer",
                      p: 1.25,
                      borderRadius: 1,
                      border: 1,
                      borderColor: active ? "primary.main" : "divider",
                      bgcolor: active ? "action.selected" : "transparent",
                      "&:hover": { bgcolor: "action.hover" },
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box sx={{ width: 10, height: 10, borderRadius: "50%", flexShrink: 0,
                        bgcolor: ESTADO_DOT[p.documentationStatus] || "grey.400" }} />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={active ? 700 : 500} noWrap>
                          {p.callName || p.callId}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {labelForStatus(ESTADO_DOC_LABELS, p.documentationStatus)} · {p.completionPercentage || 0}%
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          </Grid>

          {/* Selected postulación's documents. */}
          <Grid item xs={12} md={9}>
            {selectedPost ? (
              <ApplicantDocsValidation
                nuip={applicant.nuip}
                person={applicant}
                documents={selectedPost.documents}
                callId={selectedPost.callId}
                scope="application"
                title="Documentos de la convocatoria"
                emptyText="Esta convocatoria no pide documentos propios: se evalúa con la hoja de vida."
                onChange={applyValidation("application", selectedPost.callId)}
              />
            ) : (
              <Typography variant="body2" color="text.secondary">
                Selecciona una postulación para ver sus documentos.
              </Typography>
            )}
          </Grid>
        </Grid>
      </Paper>

      {/* Confirmación de selección: el aspirante continúa con la etapa 2 de documentos. */}
      <Dialog open={selectedOk} onClose={handleCloseSelected} maxWidth="xs" fullWidth>
        <DialogTitle>Aspirante seleccionado</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <Typography variant="body2" gutterBottom>
              {nombreCompleto || applicant.nuip} fue seleccionado(a) como contratista
              {selectedPost ? ` en «${selectedPost.callName || selectedPost.callId}»` : ""}.
            </Typography>
            <Typography variant="body2" gutterBottom>
              Ahora se le solicitarán los documentos restantes (antecedentes y documentos
              administrativos) para continuar con su vinculación.
            </Typography>
            <Typography variant="body2">
              Se le envió un correo{applicant.email ? ` a ${applicant.email}` : ""} solicitándole
              esa documentación adicional, con el enlace para cargarla. El correo{" "}
              <strong>no le informa que fue seleccionado(a)</strong>: comunícale la decisión por el
              canal formal de contratación.
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={handleCloseSelected}>Entendido</Button>
        </DialogActions>
      </Dialog>

      {/* Pestaña fija de bitácora, igual que en PQRSD. */}
      {canViewLogs && (
        <>
          <Tooltip title="Ver logs del aspirante" placement="left">
            <Button
              onClick={openLogs}
              variant="contained"
              size="small"
              sx={{
                position: "fixed",
                top: "40%",
                right: 0,
                zIndex: (theme) => theme.zIndex.drawer - 1,
                borderTopRightRadius: 0,
                borderBottomRightRadius: 0,
                minWidth: 0,
                px: 0.75,
                py: 1.5,
              }}
            >
              <ReceiptLongIcon fontSize="small" sx={{ mr: 0.5 }} />
              Logs
            </Button>
          </Tooltip>
          <ApplicantLogsDrawer
            open={logsOpen}
            onClose={() => setLogsOpen(false)}
            logs={logs}
            loading={logsLoading}
          />
        </>
      )}

      <Snackbar
        open={!!toast}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {toast && (
          <Alert severity={toast.severity} onClose={() => setToast(null)} sx={{ width: "100%" }}>
            {toast.msg}
          </Alert>
        )}
      </Snackbar>
    </Container>
  );
}
